import { ConsolidatedLocationView } from '@mark43/cad-api';
import { ListStore, ComparatorReturns, GroupingDirective } from '@list-store';
import { KeyboardControlledStore } from './hooks/use-keyboard-selection';
import { action, computed, makeObservable, observable } from 'mobx';
import { baseGroupingComparator } from '../common/helpers/list-store';
import type { LocationStoreView } from './types/locations';
import { mapConsolidatedLocationViewToLocationStoreView } from './helpers/data-mapping';
import { buildRenderedLocationList } from './helpers/build-rendered-location-list';
import type { GroupedLocationData } from './helpers/build-rendered-location-list';

type SortDirection = 'ASC' | 'DSC';

export type LocationSortOption = 'ADDRESS' | 'CROSS STREET' | 'D-AREA';
export type LocationSortMode = { option: LocationSortOption; direction: SortDirection };

type OnChange = (value: LocationStoreView | null) => void;

// There  is no displayed header for the MARK43 / some other sources, hence the placeholder.
export type LocationGroupHeader =
    | 'Results from Esri Custom'
    | 'Results from Esri'
    | 'Results from Google'
    | 'Lat / Lng Locations'
    | 'DUMMY_GROUP_HEADER'
    | 'FORCED_LOCATION';

export class LocationStore implements KeyboardControlledStore {
    private readonly store: ListStore<LocationStoreView>;

    @observable
    public sortMode: LocationSortMode | undefined;

    @observable
    public shouldScrollLocationList: boolean = false;

    @observable
    public highlightedLocationIndex: number | null = null;

    @observable
    public readonly data;

    private onChange?: OnChange;

    constructor(
        initialValue: LocationStoreView | ConsolidatedLocationView | null,
        onChange?: OnChange
    ) {
        this.store = new ListStore<LocationStoreView>();
        this.data = this.store.data;
        this.store.setGroupingDirective(this.getGroupingDirective());
        this.onChange = onChange;

        if (initialValue !== undefined && initialValue !== null) {
            const valueWithDispatchAreas =
                mapConsolidatedLocationViewToLocationStoreView(initialValue);
            this.setItems([valueWithDispatchAreas]);
        }

        makeObservable(this);
    }

    @computed
    get renderedLocationData(): GroupedLocationData {
        return buildRenderedLocationList(this.data.list);
    }

    @action
    public setSort(mode: LocationSortMode | undefined): void {
        this.sortMode = mode;

        if (mode === undefined) {
            this.store.setSortingDirective(undefined);
        } else {
            this.store.setSortingDirective({
                getSortValue: (item: LocationStoreView) => this.getSortValueForAddress(mode, item),
                comparator: (left: string, right: string) =>
                    (mode.direction === 'ASC'
                        ? left.localeCompare(right)
                        : right.localeCompare(left)) as ComparatorReturns,
            });
        }
    }

    @action
    public setItems(items: LocationStoreView[]): void {
        this.store.setItems(items);
        this.highlightedLocationIndex = 0;
    }

    @action
    public setSelectedLocation(view: ConsolidatedLocationView | null): void {
        if (view) {
            const mappedView = mapConsolidatedLocationViewToLocationStoreView(view);
            this.onChange?.(mappedView);
            return;
        }
        this.onChange?.(null);
    }

    @action
    public highlightNextItem(event: { source: 'keyboard' | 'mouse' }): void {
        if (this.highlightedLocationIndex === null) {
            this.highlightedLocationIndex = 0;
        } else if (this.highlightedLocationIndex !== this.data.list.length - 1) {
            this.highlightedLocationIndex++;
        }
        this.shouldScrollLocationList = event.source === 'keyboard';
    }

    @action
    public highlightPreviousItem(event: { source: 'keyboard' | 'mouse' }): void {
        if (this.highlightedLocationIndex === null) {
            this.highlightedLocationIndex = 0;
        } else if (this.highlightedLocationIndex !== 0) {
            this.highlightedLocationIndex--;
        }
        this.shouldScrollLocationList = event.source === 'keyboard';
    }

    @action
    public setHighlightedLocationIndex(
        index: number,
        userInputEvent?: { source: 'keyboard' | 'mouse' }
    ): void {
        this.highlightedLocationIndex = index;
        if (userInputEvent) {
            this.shouldScrollLocationList = userInputEvent.source === 'keyboard';
        }
    }

    @action
    public selectHighlightedItem(): void {
        if (this.highlightedLocationIndex !== null) {
            const location = this.renderedLocationData.renderedList[this.highlightedLocationIndex];
            if (location !== undefined) {
                this.onChange?.(location);
            }
        }
        this.highlightedLocationIndex = null;
    }

    @action
    public clearSelectedLocation(): void {
        this.onChange?.(null);
    }

    private getSortValueForAddress(mode: LocationSortMode, item: LocationStoreView): string {
        switch (mode.option) {
            case 'ADDRESS':
                return item.streetAddress ?? '';
            case 'CROSS STREET':
                return item.crossStreet1 ?? '';
            case 'D-AREA':
                return item.dispatchArea ?? '';
        }
    }

    private getGroupingDirective(): GroupingDirective<LocationStoreView> {
        return {
            getGroupValue: (item: LocationStoreView): LocationGroupHeader => {
                switch (item.source) {
                    case 'ESRI_CUSTOM':
                        return 'Results from Esri Custom';
                    case 'ESRI':
                        return 'Results from Esri';
                    case 'GOOGLE':
                        return 'Results from Google';
                    case 'LAT_LONG':
                        return 'Lat / Lng Locations';
                    case 'FORCED_LOCATION':
                        return 'FORCED_LOCATION';
                    default:
                        return 'DUMMY_GROUP_HEADER';
                }
            },
            comparator: (leftStatus: LocationGroupHeader, rightStatus: LocationGroupHeader) =>
                baseGroupingComparator(leftStatus, rightStatus, this.getSortValue),
        };
    }

    private getSortValue(header: LocationGroupHeader): number {
        switch (header) {
            case 'DUMMY_GROUP_HEADER':
                return 0;
            case 'Results from Esri Custom':
                return 1;
            case 'Results from Esri':
                return 2;
            case 'Results from Google':
                return 3;
            case 'Lat / Lng Locations':
                return 4;
            case 'FORCED_LOCATION':
                return 5;
        }
    }
}
